// src/components/global/Chatbot/ChatbotWidget.js
// Parker-portal edition — fixes applied:
//   1. access_token sent on EVERY /route call → tools never rely on expired JSON token
//   2. Session re-saved on EVERY open (not just first time) → JSON always has fresh credentials
//   3. user_type hard-locked to "parker" → partner tools are never callable
//   4. partner_secret + access_token both passed in session save payload
//   5. ✅ NEW FIX: derivePartnerSecret() now digs into partnerData.clients.secret
//      (matches actual localStorage shape: { clients: { secret: "...", id: "..." } })

import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { FaMicrophone, FaPaperPlane, FaPaperclip, FaTimes, FaSignOutAlt } from 'react-icons/fa'
import { SiGoogleassistant } from 'react-icons/si'
import './ChatbotWidget.scss'

let FASTAPI_BASE = 'http://127.0.0.1:8000'
try {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_FASTAPI_BASE) {
    FASTAPI_BASE = import.meta.env.VITE_FASTAPI_BASE
  } else if (process && process.env && process.env.REACT_APP_FASTAPI_BASE) {
    FASTAPI_BASE = process.env.REACT_APP_FASTAPI_BASE
  }
} catch (e) {}

const ROUTE_URL          = `${FASTAPI_BASE.replace(/\/$/, '')}/route`
const PUBLIC_SESSION_URL = `${FASTAPI_BASE.replace(/\/$/, '')}/public/session`
const REFRESH_URL        = `${FASTAPI_BASE.replace(/\/$/, '')}/auth/refresh`

// ✅ PARKER PORTAL: always "parker" — never changeable by any event or LS key
const PORTAL_USER_TYPE = 'parker'

export default function ChatbotWidget () {
  const [open, setOpen]                                 = useState(false)
  const [messages, setMessages]                         = useState([])
  const [input, setInput]                               = useState('')
  const [listening, setListening]                       = useState(false)
  const [slotFillingSessionId, setSlotFillingSessionId] = useState(null)
  const [isFollowup, setIsFollowup]                     = useState(false)
  const [isProcessing, setIsProcessing]                 = useState(false)

  const recognitionRef          = useRef(null)
  const transcriptSeenRef       = useRef(false)
  const transcriptTimeoutRef    = useRef(null)
  const messagesEndRef          = useRef(null)
  const inputRef                = useRef(null)
  const fileInputRef            = useRef(null)
  const ignoreSttRef            = useRef(false)
  // Tokens/user from host-app event — kept in memory, never written to LS by this component
  const openEventDetailRef      = useRef(null)
  // ✅ Track whether session has been saved THIS open-cycle.
  // Reset on every close so next open always re-saves fresh credentials.
  const sessionSavedThisOpenRef = useRef(false)

  const authState   = useSelector((s) => s?.AuthReducer || {})
  const reduxUser   = authState?.user   || authState?.userData || null
  const reduxTokens = authState?.tokens || authState?.app_tokens || null

  // ── localStorage helpers (read-only — this component never writes auth data) ─
  const _ls     = (key) => { try { return typeof window !== 'undefined' ? localStorage.getItem(key) : null } catch { return null } }
  const _lsJson = (key) => { try { const v = _ls(key); return v ? JSON.parse(v) : null } catch { return null } }

  const getLocalPartnerData = () => _lsJson('partnerData')
  const getLocalUserData    = () => {
    try {
      const raw = _ls('userData')
      if (!raw) return null
      return (raw.startsWith('{') || raw.startsWith('[')) ? JSON.parse(raw) : null
    } catch { return null }
  }

  // ── Derivation helpers ────────────────────────────────────────────────────
  const derivePartnerId = () => {
    try {
      const ev            = openEventDetailRef.current
      const localUserData = getLocalUserData()
      const partnerData   = getLocalPartnerData()
      if (ev?.user) { const u = ev.user; return String(u.id || u.user_id || u.member_id || '') || null }
      if (localUserData?.user) { const u = localUserData.user; return String(u.id || u.user_id || u.member_id || '') || null }
      if (reduxUser) return String(reduxUser.id || reduxUser.user_id || reduxUser.partner_id || '') || null
      if (partnerData) return String(partnerData.id || partnerData.user_id || partnerData.partner_id || '') || null
      return _ls('partner_id') || _ls('user_id') || _ls('userId') || null
    } catch { return null }
  }

  // ── ✅ FIXED: derivePartnerSecret now reads partnerData.clients.secret ────
  // localStorage shape observed in DevTools:
  //   partnerData = {
  //     id: 19349,
  //     clients: { id: "usm-parking19349", secret: "Y3dSKU...", partner_id: "19349", ... },
  //     ...
  //   }
  // Old code only iterated top-level keys and always returned null.
  const derivePartnerSecret = () => {
    try {
      const ev          = openEventDetailRef.current
      const partnerData = getLocalPartnerData()

      // 1️⃣ In-memory event payload (highest priority — freshest credentials)
      if (ev?.partner_secret)  return ev.partner_secret
      if (ev?.x_clientsecret)  return ev.x_clientsecret
      if (ev?.client_secret)   return ev.client_secret

      // 2️⃣ Redux store
      if (reduxTokens?.partner_secret) return reduxTokens.partner_secret
      if (reduxTokens?.client_secret)  return reduxTokens.client_secret
      if (reduxTokens?.secret)         return reduxTokens.secret
      if (authState?.partner_secret)   return authState.partner_secret

      // 3️⃣ localStorage partnerData — nested clients object FIRST, then top-level
      if (partnerData) {
        // ✅ PRIMARY: dig into partnerData.clients.secret
        const clients = partnerData.clients
        if (clients && typeof clients === 'object') {
          for (const k of ['secret', 'partner_secret', 'partnerSecret', 'client_secret', 'clientSecret']) {
            if (clients[k]) return clients[k]
          }
        }

        // ✅ SECONDARY: top-level keys on partnerData (kept as fallback)
        for (const k of ['partner_secret', 'partnerSecret', 'secret', 'client_secret', 'clientSecret']) {
          if (partnerData[k]) return partnerData[k]
        }
      }

      // 4️⃣ Raw localStorage keys
      for (const k of ['partner_secret', 'partnerSecret', 'secret', 'client_secret']) {
        const v = _ls(k); if (v) return v
      }
    } catch {}
    return null
  }

  const deriveAccessToken = () => {
    try {
      const ev = openEventDetailRef.current
      if (ev?.access_token) return ev.access_token
      if (ev?.token)        return ev.token
      if (ev?.tokens) {
        try {
          const t = typeof ev.tokens === 'string' ? JSON.parse(ev.tokens) : ev.tokens
          if (t?.access_token) return t.access_token
        } catch {}
      }
    } catch {}
    if (reduxTokens?.access_token || reduxTokens?.accessToken || reduxTokens?.token)
      return reduxTokens.access_token || reduxTokens.accessToken || reduxTokens.token
    try {
      const localUserData = getLocalUserData()
      if (localUserData?.access_token) return localUserData.access_token
    } catch {}
    for (const k of ['access_token', 'accessToken', 'token', 'app_tokens', 'auth_tokens']) {
      const v = _ls(k)
      if (!v) continue
      if ((k === 'app_tokens' || k === 'auth_tokens') && (v.startsWith('{') || v.startsWith('['))) {
        try { const p = JSON.parse(v); if (p?.access_token || p?.accessToken) return p.access_token || p.accessToken } catch {}
      }
      return v
    }
    return null
  }

  const deriveRefreshToken = () => {
    const ev = openEventDetailRef.current
    if (ev?.refresh_token) return ev.refresh_token
    return _ls('refresh_token') || _ls('refreshToken') || null
  }

  const isJwtExpired = (token) => {
    if (!token || typeof token !== 'string') return true
    const parts = token.split('.')
    if (parts.length !== 3) return true
    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
      if (!payload?.exp) return true
      return payload.exp <= Math.floor(Date.now() / 1000) + 30
    } catch { return true }
  }

  const tryRefreshAccessToken = async () => {
    try {
      const refreshToken = deriveRefreshToken()
      if (!refreshToken) return null
      const resp = await fetch(REFRESH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken, user_type: PORTAL_USER_TYPE }),
      })
      const js = resp.ok ? await resp.json().catch(() => ({})) : {}
      return js.access_token || null
    } catch { return null }
  }

  // ── Session save ──────────────────────────────────────────────────────────
  // Called on EVERY open — ensures the backend JSON always has a fresh
  // access_token AND the now-correctly-derived partner_secret.
  const saveSessionToBackend = async () => {
    try {
      let tokenNow = deriveAccessToken()

      // Try to get a fresh token if current one is expired or missing
      if (!tokenNow || isJwtExpired(tokenNow)) {
        const refreshed = await tryRefreshAccessToken()
        if (refreshed) tokenNow = refreshed
      }

      if (!tokenNow) {
        console.warn('[Chatbot] No valid token — session save skipped')
        return { ok: false, reason: 'no_token' }
      }

      const partnerId     = derivePartnerId()
      // ✅ Now correctly reads partnerData.clients.secret
      const partnerSecret = derivePartnerSecret()

      // Always overwrite user_type in LS to "parker" on every save
      try { localStorage.setItem('user_type', PORTAL_USER_TYPE) } catch {}

      const ev            = openEventDetailRef.current
      const localUserData = getLocalUserData()
      const userBlock     = ev?.user || localUserData?.user || getLocalPartnerData() || undefined

      const payload = {
        partner_id:     partnerId,
        tokens:         { access_token: tokenNow },
        access_token:   tokenNow,
        partner_secret: partnerSecret || undefined,
        user:           userBlock,
        ttl_seconds:    7200,
        force_file:     true,
        user_type:      PORTAL_USER_TYPE,
      }
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k])

      console.info('[Chatbot] public/session ->', PUBLIC_SESSION_URL, {
        ...payload,
        tokens:         { access_token: '<REDACTED>' },
        partner_secret: payload.partner_secret ? '<REDACTED>' : undefined,
        access_token:   '<REDACTED>',
      })

      const resp = await fetch(PUBLIC_SESSION_URL, {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })

      const data = await resp.json().catch(() => ({}))

      if (!resp.ok) {
        console.warn('[Chatbot] public/session failed', resp.status, data)
        return { ok: false, status: resp.status, data }
      }

      sessionSavedThisOpenRef.current = true
      console.info('[Chatbot] Session saved ✅  partner_id:', partnerId, '| partner_secret:', partnerSecret ? 'present' : 'MISSING ⚠️')
      return { ok: true, data }
    } catch (err) {
      console.error('[Chatbot] saveSession error:', err)
      return { ok: false, error: String(err) }
    }
  }

  // ── Send message ──────────────────────────────────────────────────────────
  const sendToBackend = async (text) => {
    const trimmed = (text || '').trim()
    if (!trimmed) return
    setMessages(prev => [...prev, { sender: 'user', text: trimmed }])
    setIsProcessing(true)

    try {
      const dPartnerId     = derivePartnerId()
      // ✅ Now correctly reads partnerData.clients.secret
      const dPartnerSecret = derivePartnerSecret()
      let tokenNow = deriveAccessToken()
      if (!tokenNow || isJwtExpired(tokenNow)) {
        const refreshed = await tryRefreshAccessToken()
        if (refreshed) tokenNow = refreshed
      }

      const payload = {
        query:        trimmed,
        is_followup:  Boolean(isFollowup),
        // Always "parker" — backend _is_tool_allowed() blocks partner tools
        user_type:    PORTAL_USER_TYPE,
        // Send fresh access_token on EVERY request — mcp_module.py picks it
        // up from the payload BEFORE falling back to the (possibly stale) stored JSON
        access_token: tokenNow || undefined,
      }
      if (dPartnerId !== null && dPartnerId !== undefined) payload.partner_id = dPartnerId
      if (dPartnerSecret)       payload.partner_secret = dPartnerSecret
      if (slotFillingSessionId) payload.session_id     = slotFillingSessionId

      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' }
      if (tokenNow) headers.Authorization = `Bearer ${tokenNow}`

      console.info('[Chatbot] /route ->', {
        ...payload,
        partner_secret: payload.partner_secret ? '<REDACTED>' : 'MISSING ⚠️',
        access_token:   payload.access_token   ? '<REDACTED>' : 'MISSING ⚠️',
        auth:           !!tokenNow,
      })

      const resp = await fetch(ROUTE_URL, { method: 'POST', mode: 'cors', headers, body: JSON.stringify(payload) })
      const data = await resp.json().catch(() => ({}))

      if (!resp.ok) {
        console.warn('[Chatbot] /route error', resp.status, data)
        setMessages(prev => [...prev, {
          sender: 'bot',
          text:   'Something went wrong. Please try again.',
        }])
        setSlotFillingSessionId(null)
        setIsFollowup(false)
        return
      }

      if (data.status === 'needs_input') {
        setMessages(prev => [...prev, { sender: 'bot', text: data.question || 'I need more info.', isQuestion: true }])
        setSlotFillingSessionId(data.session_id || null)
        setIsFollowup(true)
      } else if (data.status === 'success') {
        setMessages(prev => [...prev, { sender: 'bot', text: data.answer || 'Done.' }])
        setSlotFillingSessionId(null)
        setIsFollowup(false)
      } else {
        setMessages(prev => [...prev, { sender: 'bot', text: data.answer || data.message || 'Unexpected response.' }])
        setSlotFillingSessionId(null)
        setIsFollowup(false)
      }
    } catch (err) {
      console.error('[Chatbot] sendToBackend error:', err)
      setMessages(prev => [...prev, { sender: 'bot', text: "Can't reach the server right now. Please try again." }])
      setSlotFillingSessionId(null)
      setIsFollowup(false)
    } finally {
      setIsProcessing(false)
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
    }
  }

  const handleSend = (e, programmaticText) => {
    e?.preventDefault?.()
    const text = typeof programmaticText === 'string' ? programmaticText : input
    if (!text?.trim()) return
    if (!programmaticText) setInput('')
    sendToBackend(text)
  }

  // ── Logout ────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    if (!window.confirm('Are you sure you want to log out?')) return
    try {
      const keysToRemove = ['access_token', 'refresh_token', 'partner_secret', 'partnerData', 'userData', 'user_type']
      keysToRemove.forEach(k => { try { localStorage.removeItem(k) } catch {} })
      const allKeys = Object.keys(localStorage)
      allKeys
        .filter(k => k.startsWith('chat_session_saved:') || k.startsWith('partner_user_storage:'))
        .forEach(k => { try { localStorage.removeItem(k) } catch {} })
      openEventDetailRef.current = null
    } catch {}
    setMessages([])
    setInput('')
    setOpen(false)
    setSlotFillingSessionId(null)
    setIsFollowup(false)
    sessionSavedThisOpenRef.current = false
  }

  // ── Speech recognition ────────────────────────────────────────────────────
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) { recognitionRef.current = null; return }
    const rec = new SR()
    rec.continuous     = false
    rec.interimResults = true
    rec.lang           = 'en-US'
    rec.onresult = (event) => {
      if (ignoreSttRef.current) return
      let interim = '', final = ''
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const r = event.results[i]
        if (r.isFinal) final   += r[0].transcript
        else           interim += r[0].transcript
      }
      if (interim || final) {
        transcriptSeenRef.current = true
        if (transcriptTimeoutRef.current) { clearTimeout(transcriptTimeoutRef.current); transcriptTimeoutRef.current = null }
      }
      if (interim) setInput(interim)
      if (final)   setInput(final.trim())
    }
    rec.onerror = () => setListening(false)
    rec.onend   = () => { setListening(false); ignoreSttRef.current = false }
    rec.onstart = () => setListening(true)
    recognitionRef.current = rec
    return () => { recognitionRef.current = null }
  }, [])

  const handleMicToggle = () => {
    if (!recognitionRef.current) { alert('Speech recognition not available'); return }
    if (listening) { try { recognitionRef.current.stop() } catch {} setListening(false); return }
    ignoreSttRef.current      = false
    transcriptSeenRef.current = false
    if (transcriptTimeoutRef.current) clearTimeout(transcriptTimeoutRef.current)
    transcriptTimeoutRef.current = setTimeout(() => {
      if (!transcriptSeenRef.current) try { recognitionRef.current.stop() } catch {}
    }, 3000)
    try { recognitionRef.current.start() } catch (err) { console.warn('Mic start failed', err) }
  }

  // ── open-chatbot event from host app ─────────────────────────────────────
  useEffect(() => {
    const handler = (ev) => {
      try {
        if (ev?.detail) {
          openEventDetailRef.current = ev.detail
          // Always lock user_type to parker regardless of what the event says
          try { localStorage.setItem('user_type', PORTAL_USER_TYPE) } catch {}
        }
      } catch (e) { console.warn('[Chatbot] open-chatbot event error', e) }
      setOpen(true)
      sessionSavedThisOpenRef.current = false  // force re-save on this open
      saveSessionToBackend()
      const q    = ev?.detail?.query
      const auto = !!ev?.detail?.autoSend
      if (q) { setInput(q); if (auto) setTimeout(() => handleSend(null, q), 60) }
    }
    window.addEventListener('open-chatbot', handler)
    return () => window.removeEventListener('open-chatbot', handler)
  }, [])

  // Re-save on every open
  useEffect(() => {
    if (open) {
      sessionSavedThisOpenRef.current = false
      saveSessionToBackend()
    } else {
      // Reset flag when closed so next open always saves again
      sessionSavedThisOpenRef.current = false
    }
  }, [open])

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, open])

  const handleTriggerClick = async () => {
    sessionSavedThisOpenRef.current = false
    setOpen(true)
    // saveSessionToBackend fires via the `open` useEffect above
  }

  // ── SVG car icon (clean, professional — not emoji/cartoon) ──────────────
  const CarIcon = ({ size = 22, color = 'white' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 11L6.5 6.5C6.8 5.6 7.65 5 8.6 5H15.4C16.35 5 17.2 5.6 17.5 6.5L19 11" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <rect x="2" y="11" width="20" height="7" rx="2" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.6"/>
      <circle cx="7" cy="18.5" r="1.8" fill={color}/>
      <circle cx="17" cy="18.5" r="1.8" fill={color}/>
      <path d="M2 14h20" stroke={color} strokeWidth="1.2" strokeOpacity="0.5"/>
      <path d="M9 8.5h6" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeOpacity="0.7"/>
    </svg>
  )

  // ── Suggested questions (shown on empty state) ───────────────────────────
  const SUGGESTED = [
    { icon: '🅿️', text: 'How can I request for a parking permit?' },
    { icon: '📅', text: 'Give me the steps to make a new reservation in advance' },
    { icon: '🎫', text: 'If I lose my ticket, can I exit?' },
    { icon: '🧾', text: 'Get me the receipt for the ticket' },
    { icon: '🚫', text: 'I got a challan on the ticket, can you cancel it?' },
  ]

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {!open && (
        <button onClick={handleTriggerClick} className="chatbot-trigger" title="Open Parky">
          <CarIcon size={26} color="white" />
        </button>
      )}

      {open && (
        <div className="chatbot-panel">

          {/* ── Header ── */}
          <div className="chatbot-header">
            <div className="chatbot-title">
              <div className="parky-avatar-wrap">
                <div className="parky-avatar">
                  <CarIcon size={22} color="white" />
                </div>
                <div className="parky-status-dot" />
              </div>
              <div className="title-info">
                <div className="title-name">Parky</div>
                <div className="title-sub">Your parking assistant · Always here</div>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button onClick={() => setOpen(false)} className="close-btn" title="Close">
                <FaTimes size={13} />
              </button>
            </div>
          </div>

          {/* ── Messages ── */}
          <div className="chatbot-body">
            {messages.length === 0 && (
              <div className="welcome-screen">
                <div className="welcome-avatar">
                  <CarIcon size={32} color="white" />
                </div>
                <div className="welcome-title">Hi there! I'm Parky 👋</div>
                <div className="welcome-sub">
                  Your smart parking assistant. Ask me anything about permits, reservations, tickets, or receipts.
                </div>
                <div className="suggested-label">
                  <span>★</span> SUGGESTED QUESTIONS
                </div>
                <div className="suggested-list">
                  {SUGGESTED.map((s, i) => (
                    <button
                      key={i}
                      className="suggested-item"
                      onClick={() => {
                        // FIX: don't put text in input — send directly and keep input empty
                        sendToBackend(s.text)
                      }}
                    >
                      <span className="suggested-icon">{s.icon}</span>
                      <span className="suggested-text">{s.text}</span>
                      <span className="suggested-arrow">›</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, idx) => (
              <div key={idx} className={`msg-row ${m.sender === 'user' ? 'msg-user' : 'msg-bot'}`}>
                <div
                  className={`msg-bubble ${
                    m.sender === 'user'
                      ? 'user-bubble'
                      : m.isQuestion
                      ? 'question-bubble'
                      : 'bot-bubble'
                  }`}
                >

                  {(() => {

                      // ==========================================
                      // QR Code PDF Response
                      // ==========================================
                      if (
                        typeof m.text === "object" &&
                        m.text !== null &&
                        m.text.pdf_base64
                      ) {

                        const downloadPdf = () => {

                          const binary = atob(m.text.pdf_base64)

                          const bytes = Uint8Array.from(
                            binary,
                            c => c.charCodeAt(0)
                          )

                          const blob = new Blob(
                            [bytes],
                            { type: "application/pdf" }
                          )

                          const url = URL.createObjectURL(blob)

                          const a = document.createElement("a")

                          a.href = url
                          a.download = "parking_qrcode.pdf"

                          document.body.appendChild(a)
                          a.click()
                          document.body.removeChild(a)

                          URL.revokeObjectURL(url)
                        }

                        return (
                          <>
                            <div>{m.text.message}</div>

                            <button
                              type="button"
                              className="download-btn"
                              onClick={downloadPdf}
                            >
                              Download QR Code
                            </button>
                          </>
                        )
                      }

                      // ==========================================
                      // Existing Receipt URL Response
                      // ==========================================
                      const textValue =
                        typeof m.text === "string"
                          ? m.text
                          : (m.text?.message || "")

                      const regex = /(https?:\/\/[^\s]+)/g

                      const match = textValue.match(regex)

                      if (!match) {
                        return textValue
                      }

                      const url = match[0]

                      const messageText =
                        textValue.replace(url, "").trim()

                      return (
                        <>
                          <div>{messageText}</div>

                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-btn"
                          >
                            Download Receipt
                          </a>
                        </>
                      )
                    })()}

                </div>
              </div>
            ))}


            {/* Typing dots */}
            {isProcessing && !listening && (
              <div className="msg-row msg-bot">
                <div className="typing-bubble">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* ── Footer ── */}
          <div className="chatbot-footer-wrap">

            {/* FIX: Listening popup floats ABOVE the mic button */}
            {listening && (
              <div className="listening-popup">
                <div className="listening-label">
                  <span className="listening-dot" />
                  LISTENING…
                </div>
                <div className="waveform">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="waveform-bar" />
                  ))}
                </div>
                <button
                  type="button"
                  className="stop-btn"
                  onClick={() => { try { recognitionRef.current?.stop() } catch {} setListening(false) }}
                >
                  Stop
                </button>
              </div>
            )}

            <form onSubmit={handleSend} className="chatbot-footer">
              {/* Clip on left */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="file-btn"
                title="Attach file"
              >
                <FaPaperclip size={14} />
              </button>

              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value)
                  e.target.style.height = 'auto'
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e) } }}
                placeholder={isFollowup ? 'Please provide the requested information...' : 'Ask me anything about parking...'}
                className="chat-input"
                disabled={isProcessing}
              />

              {/* Mic + Send on right */}
              <div className="footer-btns">
                <button
                  type="button"
                  onClick={handleMicToggle}
                  className={`mic-btn${listening ? ' active' : ''}`}
                  title={listening ? 'Stop listening' : 'Voice input'}
                >
                  <FaMicrophone size={14} />
                </button>
                <button
                  type="submit"
                  className="send-btn"
                  title="Send"
                  disabled={isProcessing || !input.trim()}
                >
                  <FaPaperPlane size={13} />
                </button>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) console.log('[Chatbot] Attachment:', f.name) }}
              />
            </form>

            <div className="chatbot-powered">
              Powered by <span>ParkEngage AI</span>
            </div>
          </div>

        </div>
      )}
    </>
  )
}